// @ts-check

/**
 * /max — Mobius Max scrollytelling landing page.
 *
 * Vanilla port of the v0 prototype (React/framer-motion/R3F). One shared rAF
 * loop drives every scroll-mapped value; the Mobius ribbon is a plain three.js
 * scene (instanced fins + PMREM environment) on a single fixed canvas.
 *
 * Layout contract with max.scss / max.html:
 *   .max-backdrop  fixed z-0  (sunrise -> ends WHITE post-hero)
 *   .max-tint      fixed z-0  (fades WHITE -> beige near the quote section)
 *   .max-persistent fixed z-2 (the ONE WebGL Max, above both backdrops)
 *   hero stage (sticky z-10) / section content paint above.
 */

import * as THREE from 'three';
import {RoundedBoxGeometry} from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

(function() {
    'use strict';

    const page = document.getElementById('max-page');
    if (!page) {
        return;
    }

    // Always cold-start /max at the top, like a fresh navigation. This is a
    // scroll-driven scene (every beat is a function of scroll position), and
    // resuming mid-scroll from the browser's restored position cold-starts the
    // whole timeline at an arbitrary point — the WebGL ribbon, sunrise, tint
    // and beat opacities all have to jump to a deep state before their
    // measurements/springs have settled, which is where the reload-while-
    // scrolled glitches come from. Opting out of scroll restoration removes the
    // entire failure class: the page begins at 0 every load and plays forward.
    if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    // Backstop: some browsers restore on the load event, after this deferred
    // script has already run — force the top again once, then stop.
    window.addEventListener('load', () => window.scrollTo(0, 0), {once: true});

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ── Small animation helpers ──────────────────────────────────────────

    const clamp01 = (v) => Math.min(1, Math.max(0, v));
    const lerp = (a, b, t) => a + (b - a) * t;

    /**
     * Piecewise-linear mapping (framer's useTransform): map `v` through sorted
     * input stops to output stops, clamped at the ends.
     *
     * @param {number} v - Input value.
     * @param {number[]} stops - Sorted input stops.
     * @param {number[]} out - Output values, same length.
     * @returns {number} Mapped value.
     */
    function interp(v, stops, out) {
        if (v <= stops[0]) {
            return out[0];
        }
        for (let i = 0; i < stops.length - 1; i++) {
            if (v < stops[i + 1]) {
                const t = (v - stops[i]) / (stops[i + 1] - stops[i]);
                return lerp(out[i], out[i + 1], t);
            }
        }
        return out[out.length - 1];
    }

    /**
     * Critically-tuned spring smoother (framer's useSpring). Integrated once
     * per frame from the shared loop.
     */
    class Spring {
        /**
         * @param {number} stiffness - Spring stiffness.
         * @param {number} damping - Spring damping.
         * @param {number} mass - Spring mass.
         * @param {number} [initial] - Starting value.
         */
        constructor(stiffness, damping, mass, initial = 0) {
            this.stiffness = stiffness;
            this.damping = damping;
            this.mass = mass;
            this.target = initial;
            this.value = initial;
            this.velocity = 0;
        }

        /**
         * @param {number} dt - Seconds since last step.
         * @returns {number} The smoothed value.
         */
        step(dt) {
            // Sub-step for stability at low frame rates.
            const n = Math.max(1, Math.ceil(dt / 0.008));
            const h = dt / n;
            for (let i = 0; i < n; i++) {
                const accel = ((this.target - this.value) * this.stiffness - this.damping * this.velocity) / this.mass;
                this.velocity += accel * h;
                this.value += this.velocity * h;
            }
            return this.value;
        }
    }

    /**
     * Cubic bezier easing solver (for the drum's turn ease).
     *
     * @param {number} x1 - First control point x.
     * @param {number} y1 - First control point y.
     * @param {number} x2 - Second control point x.
     * @param {number} y2 - Second control point y.
     * @returns {(t: number) => number} Easing function.
     */
    function cubicBezier(x1, y1, x2, y2) {
        const cx = 3 * x1;
        const bx = 3 * (x2 - x1) - cx;
        const ax = 1 - cx - bx;
        const cy = 3 * y1;
        const by = 3 * (y2 - y1) - cy;
        const ay = 1 - cy - by;
        const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
        const sampleY = (t) => ((ay * t + by) * t + cy) * t;
        const sampleDX = (t) => (3 * ax * t + 2 * bx) * t + cx;
        return (x) => {
            let t = x;
            for (let i = 0; i < 6; i++) {
                const err = sampleX(t) - x;
                const d = sampleDX(t);
                if (Math.abs(err) < 1e-5 || d === 0) {
                    break;
                }
                t -= err / d;
            }
            return sampleY(Math.min(1, Math.max(0, t)));
        };
    }

    // ── Shared scroll state ──────────────────────────────────────────────

    const heroSection = document.getElementById('max-hero');
    const quoteSection = document.getElementById('quote-section');
    const badgeSection = document.getElementById('badge-cutout');

    let isMobile = window.innerWidth < 768;

    // Freeze the viewport height used as the progress denominator; only
    // refresh when the WIDTH changes so the collapsing mobile browser toolbar
    // can't make progress leap mid-scroll.
    //
    // Read the LAYOUT viewport (documentElement.clientHeight), not the VISUAL
    // viewport (window.innerHeight). Progress is `-rect.top / (offsetHeight -
    // viewportH)`, and rect.top / offsetHeight (500vh) are layout-space values
    // that pinch-zoom leaves untouched. window.innerHeight, however, shrinks
    // during a pinch-zoom, so mixing it in swings the denominator while the
    // numerator holds still — the beats and sunrise layers then flail even
    // though nothing scrolled. clientHeight keeps both sides in the same space.
    let viewportH = document.documentElement.clientHeight;
    let lastWidth = window.innerWidth;

    // Hero smoothing. The desktop tuning (k=120, m=0.4 -> natural frequency
    // ~17 rad/s at a damping ratio of ~2.2) is deliberately overdamped so a
    // mouse wheel's discrete clicks read as continuous motion. On a touch
    // screen the finger IS the scrubber, so that same lag reads as dragging
    // something heavy through the sunrise and prompt beats. Mobile stiffens
    // and lightens the spring (k=200, m=0.25 -> ~28 rad/s, ~1.6x quicker to
    // track) while holding the damping ratio at ~2.1 — still no overshoot or
    // bounce, just less viscosity between finger and scene. Only the hero
    // spring changes; the tint/badge springs drive later sections.
    const HERO_SPRING = {stiffness: 120, damping: 30, mass: 0.4};
    const HERO_SPRING_TOUCH = {stiffness: 200, damping: 30, mass: 0.25};

    const heroProgress = new Spring(HERO_SPRING.stiffness, HERO_SPRING.damping, HERO_SPRING.mass);
    const tintOpacity = new Spring(140, 30, 0.4);
    const badgeSettle = new Spring(120, 30, 0.4);

    /** Apply the hero spring tuning for the current breakpoint. */
    function tuneHeroSpring() {
        const s = isMobile ? HERO_SPRING_TOUCH : HERO_SPRING;
        heroProgress.stiffness = s.stiffness;
        heroProgress.damping = s.damping;
        heroProgress.mass = s.mass;
    }

    tuneHeroSpring();

    let rawBlend = 0;
    // Set true by readScroll when the scroll position jumps discontinuously
    // (Chrome's scroll restoration on reload-deep, an in-page anchor jump, or a
    // very fast fling). The frame loop then snaps the springs straight to the
    // new target instead of easing — otherwise the smoothing spring would
    // animate ACROSS the whole hero and flash every intermediate beat (wrong
    // headline / prompts for your actual scroll position) on the way. Normal
    // incremental scrolling produces small per-event deltas and never trips it,
    // so the storytelling stays smoothly spring-smoothed.
    let snapSprings = false;
    let prevHeroTarget = 0;

    // While a resize/zoom gesture is in flight, the browser interleaves layout
    // passes with scroll-anchoring corrections, so consecutive events read
    // CONTRADICTORY geometry — progress spikes on one event and corrects on the
    // next. Easing through that jitter smears the beats; snapping to it renders
    // the raw oscillation (both read as violent flicker). So while events keep
    // arriving we FREEZE every scroll-derived target — the scene simply holds —
    // and when the storm settles (no event for 180ms) we snap once, cleanly, to
    // wherever the new layout actually put us. `resizeFrozen` is the frame
    // loop's memory of the previous frame's state so it can detect the release
    // edge.

    let resizeFreezeUntil = 0;

    let resizeFrozen = false;

    function readScroll() {
        if (performance.now() < resizeFreezeUntil) {
            return;
        }
        if (heroSection) {
            const rect = heroSection.getBoundingClientRect();
            const scrollable = heroSection.offsetHeight - viewportH;
            heroProgress.target = scrollable > 0 ? clamp01(-rect.top / scrollable) : 0;
            // A jump larger than a fast human fling (~0.1 of the hero per event)
            // is a discontinuity — snap rather than ease through the beats.
            if (Math.abs(heroProgress.target - prevHeroTarget) > 0.25) {
                snapSprings = true;
            }
            prevHeroTarget = heroProgress.target;
        }
        const vh = document.documentElement.clientHeight || 1;
        if (quoteSection) {
            // 0 when the quote section top is one viewport below the fold, 1
            // once it reaches the top of the viewport.
            rawBlend = clamp01(1 - quoteSection.getBoundingClientRect().top / vh);
            tintOpacity.target = rawBlend;
        }
        if (badgeSection) {
            const top = badgeSection.getBoundingClientRect().top;
            badgeSettle.target = clamp01((vh - top) / (vh * 0.7));
        }
    }

    window.addEventListener('scroll', readScroll, {passive: true});

    // Touch devices collapse/expand the browser chrome (URL bar) DURING normal
    // scrolling, firing height-only resizes the whole way down the page.
    const coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    // A resize is a DISCONTINUITY, not motion. Every window-resize or
    // pinch-zoom event extends the freeze window; the frame loop holds the
    // scene steady while events keep arriving and performs the single
    // snap-to-truth once they stop (see the release edge in frame()).
    //
    // EXCEPT on touch devices for height-only events: those are the toolbar
    // riding along with the scroll, not a window gesture. viewportH is already
    // frozen against them (width-gated below), so the targets stay stable on
    // their own — engaging the freeze would instead hitch the scroll-driven
    // scene for 180ms and then snap it on every toolbar transition (the
    // "choppy and jumpy" mobile scroll). Width changes (rotate, split-screen)
    // still freeze; desktop keeps the freeze for all resizes, where a
    // height-only event means a real window-edge drag.
    function onViewportResize() {
        const widthChanged = window.innerWidth !== lastWidth;
        if (widthChanged) {
            lastWidth = window.innerWidth;
            viewportH = document.documentElement.clientHeight;
            isMobile = window.innerWidth < 768;
            tuneHeroSpring();
        }
        if (widthChanged || !coarsePointer) {
            resizeFreezeUntil = performance.now() + 180;
        }
    }

    window.addEventListener('resize', onViewportResize);
    // Pinch-zoom moves the VISUAL viewport; some engines fire only
    // visualViewport.resize for it (window.resize stays silent), so listen to
    // both. onViewportResize is idempotent — double-firing is harmless.
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', onViewportResize);
    }
    readScroll();
    // First paint starts at the real position (deep links / reload mid-page).
    heroProgress.value = heroProgress.target;
    tintOpacity.value = tintOpacity.target;
    badgeSettle.value = badgeSettle.target;

    // ── Beat 1 intro copy: binary + reversible with hysteresis ───────────

    const intro = document.getElementById('max-intro');
    let introVisible = true;

    function updateIntroVisibility() {
        if (!heroSection || !intro) {
            return;
        }
        // Same freeze as readScroll: mid-resize geometry is contradictory, and
        // this toggle is binary — flip-flopping it is the most visible flicker
        // of all. The release edge in frame() re-runs it with settled layout.
        if (performance.now() < resizeFreezeUntil) {
            return;
        }
        const scrollable = Math.max(0, heroSection.offsetHeight - document.documentElement.clientHeight);
        const sectionTop = heroSection.getBoundingClientRect().top + window.scrollY;
        const y = window.scrollY - sectionTop;
        if (y > scrollable * 0.14) {
            introVisible = false;
        } else if (y < scrollable * 0.1) {
            introVisible = true;
        }
        intro.style.opacity = introVisible ? '1' : '0';
    }

    window.addEventListener('scroll', updateIntroVisibility, {passive: true});
    window.addEventListener('resize', updateIntroVisibility);

    // ── Adaptive nav theming ─────────────────────────────────────────────
    // The site nav can't theme itself here: the hero is a single section, but
    // its fixed backdrop animates from near-black to daylight-white mid-scroll,
    // so the section-based nav themer would keep one color the whole hero and
    // leave white text stranded on white once the sunrise blooms. We drive it
    // from scroll progress instead — light text while the backdrop behind the
    // nav is dark, dark text once daylight whitens it (and through every light
    // section below). The `is-max-nav-over-dark` class is styled only under
    // body.max-page-body, so no other page's nav is affected.
    const navEl = document.querySelector('header.nav') || document.querySelector('.nav');
    let navOverDark = true;
    if (navEl) {
        navEl.classList.add('is-max-nav-over-dark');
    }
    updateIntroVisibility();

    // ── The sky: sunrise gradients + daylight wash, on ONE canvas ────────
    //
    // WHY A CANVAS. This used to be 8 DOM layers (6 sunrise + daylight +
    // white lock), each `inset: -25%` (= 150% of the viewport in both axes)
    // with `will-change: transform`, transform-scaled from 1x up to 5.2x.
    // Measured at 1440x900 DPR2 that is 8 composited layers of 2160x1350 CSS
    // px — ~44.5MB of raster each, ~412MB total. Chrome rasterizes composited
    // layers in TILES at a chosen raster scale; scaling that far forces
    // per-tile re-raster (re-rastering one layer at 5.2x would be ~1.26GB, so
    // Chrome caps, defers and evicts instead). While the page idles Chrome
    // reclaims those giant layers' tiles; a resumed scroll then re-rasters
    // progressively, showing some tiles at the old raster scale and some at
    // the new. Tiles are rectangles, so the seams read as hard rectangular
    // edges in the sky, flickering as they fill in. That is the bug.
    //
    // Painting the same gradients into one small canvas removes the mechanism
    // rather than lowering its odds: 1 layer instead of 8, ~1MB instead of
    // ~412MB, no compositor raster-scale decisions, no tiles of animated
    // vector content, nothing to evict mid-animation. Growth becomes a radius
    // number in the draw call, so it is also sharper than transform-scaling a
    // rasterized gradient 5.2x ever was.
    //
    // `motion` owns the scroll growth / breathing / drift (unchanged from the
    // DOM version); `paint` owns what used to live in the CSS: the gradient
    // colour, its radii as a fraction of the layer box, the vertical origin,
    // and the entrance stagger.
    const SUNRISE = [
        {growStart: 0.10, growEnd: 0.54, growTo: 2.2, pulsePeriod: 11, pulseAmp: 0.05, phase: 0.0, driftAmp: -18,
            rgb: [1, 26, 72], sizeX: 1.40, sizeY: 1.20, originY: 1.00, delay: 0.00},
        {growStart: 0.08, growEnd: 0.52, growTo: 2.6, pulsePeriod: 9.5, pulseAmp: 0.06, phase: 1.1, driftAmp: 14,
            rgb: [20, 91, 168], sizeX: 1.20, sizeY: 1.00, originY: 1.00, delay: 0.12},
        {growStart: 0.06, growEnd: 0.50, growTo: 3.0, pulsePeriod: 8, pulseAmp: 0.07, phase: 2.3, driftAmp: -12,
            rgb: [27, 117, 205], sizeX: 1.00, sizeY: 0.85, originY: 1.00, delay: 0.24},
        {growStart: 0.05, growEnd: 0.48, growTo: 3.6, pulsePeriod: 7, pulseAmp: 0.08, phase: 3.5, driftAmp: 16,
            rgb: [227, 192, 206], sizeX: 0.85, sizeY: 0.68, originY: 1.02, delay: 0.36},
        {growStart: 0.04, growEnd: 0.46, growTo: 4.4, pulsePeriod: 6, pulseAmp: 0.10, phase: 4.7, driftAmp: -10,
            rgb: [253, 135, 1], sizeX: 0.70, sizeY: 0.52, originY: 1.04, delay: 0.48},
        {growStart: 0.02, growEnd: 0.44, growTo: 5.2, pulsePeriod: 5, pulseAmp: 0.12, phase: 5.6, driftAmp: 12,
            rgb: [253, 154, 1], sizeX: 0.52, sizeY: 0.40, originY: 1.06, delay: 0.60},
    ];

    // Alpha ramps, ported verbatim from the CSS stops (fraction of radius ->
    // alpha multiplier). The feathered tails are what used to be a 28px blur.
    const SUNRISE_STOPS = [[0, 1], [0.48, 0.226], [0.55, 0.113], [0.61, 0.035], [0.68, 0]];
    const DAYLIGHT_STOPS = [[0, 1], [0.40, 0.85], [0.60, 0.24], [0.65, 0.09], [0.68, 0.03], [0.74, 0]];

    // The old layer box was the -25% inset wrapper: 1.5x the sunrise box on
    // both axes, with transform-origin at its bottom centre. Everything below
    // is expressed against that same box so the geometry is identical.
    const SKY_BOX = 1.5;
    // Entrance: `opacity 0->1, translateY(42vh)->0` over 2.2s on the old
    // .max-sunrise-layer__fill transition, staggered by `delay`.
    const SKY_ENTRANCE_SEC = 2.2;
    const SKY_ENTRANCE_SHIFT = 0.42;
    const skyEase = cubicBezier(0.22, 1, 0.36, 1);
    // Gradients are smooth, so a half-resolution backing store upscales
    // indistinguishably (and dithers away banding). Keeps the buffer ~1MB.
    const SKY_RENDER_SCALE = 0.5;

    const skyCanvas = /** @type {HTMLCanvasElement|null} */ (document.getElementById('max-sky'));
    const skyCtx = skyCanvas ? skyCanvas.getContext('2d') : null;
    let skyW = 0;
    let skyH = 0;

    const glow = document.getElementById('max-glow');

    /** Match the backing store to the canvas' CSS box at SKY_RENDER_SCALE. */
    function sizeSky() {
        if (!skyCanvas || !skyCtx) {
            return;
        }
        const w = skyCanvas.clientWidth;
        const h = skyCanvas.clientHeight;
        if (w < 1 || h < 1) {
            return;
        }
        skyW = w;
        skyH = h;
        const bw = Math.max(2, Math.round(w * SKY_RENDER_SCALE));
        const bh = Math.max(2, Math.round(h * SKY_RENDER_SCALE));
        // Assigning width/height CLEARS the canvas, so only touch it when the
        // size actually changed — and every caller redraws in the same tick,
        // so a cleared canvas is never presented.
        if (skyCanvas.width !== bw || skyCanvas.height !== bh) {
            skyCanvas.width = bw;
            skyCanvas.height = bh;
        }
        // Draw in CSS-pixel coordinates; the browser upscales the buffer.
        skyCtx.setTransform(bw / w, 0, 0, bh / h, 0, 0);
    }

    /**
     * Fill the canvas with one elliptical radial gradient. Canvas gradients
     * are circular, so scale the space to turn the unit circle into the
     * ellipse CSS would have drawn.
     *
     * @param {number} cx - Centre x, CSS px.
     * @param {number} cy - Centre y, CSS px.
     * @param {number} rx - Horizontal radius, CSS px.
     * @param {number} ry - Vertical radius, CSS px.
     * @param {number[]} rgb - Base colour.
     * @param {number[][]} stops - [offset, alphaMultiplier] pairs.
     * @param {number} alpha - Layer opacity.
     */
    function paintSkyEllipse(cx, cy, rx, ry, rgb, stops, alpha) {
        if (!skyCtx || rx <= 0.01 || ry <= 0.01 || alpha <= 0.001) {
            return;
        }
        skyCtx.save();
        skyCtx.translate(cx, cy);
        skyCtx.scale(rx, ry);
        const grad = skyCtx.createRadialGradient(0, 0, 0, 0, 0, 1);
        for (let s = 0; s < stops.length; s++) {
            const a = stops[s][1] * alpha;
            grad.addColorStop(stops[s][0], `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a.toFixed(4)})`);
        }
        skyCtx.fillStyle = grad;
        // Cover the whole canvas, expressed in the scaled local space.
        const x0 = -cx / rx;
        const y0 = -cy / ry;
        skyCtx.fillRect(x0, y0, (skyW - cx) / rx - x0, (skyH - cy) / ry - y0);
        skyCtx.restore();
    }

    /**
     * Repaint the sky for the current scroll progress.
     *
     * @param {number} p - Smoothed hero progress 0 -> 1.
     * @param {number} now - rAF timestamp, ms.
     * @param {boolean} frozen - True mid resize/zoom storm.
     */
    function drawSky(p, now, frozen) {
        if (!skyCanvas || !skyCtx) {
            return;
        }
        // Self-heal the backing store, but never mid-storm: during a window
        // drag the canvas just stretches (smooth, no flash) instead of
        // reallocating every frame. Height is driven by the sunrise box's
        // 100lvh, so a mobile toolbar collapse never lands here at all.
        if (!frozen && (skyCanvas.clientWidth !== skyW || skyCanvas.clientHeight !== skyH)) {
            sizeSky();
        }
        if (skyW < 1 || skyH < 1) {
            return;
        }

        const W = skyW;
        const H = skyH;
        const boxW = W * SKY_BOX;
        const boxH = H * SKY_BOX;
        // Bottom centre of the old -25% layer box, in canvas coordinates.
        const originY = H * 1.25;

        skyCtx.clearRect(0, 0, W, H);

        const t = now / 1000;
        for (let i = 0; i < SUNRISE.length; i++) {
            const L = SUNRISE[i];
            // Entrance (was a CSS transition gated on .is-started).
            let entrance = 0;
            let shift = 0;
            if (startedAt !== 0) {
                const secs = (now - startedAt) / 1000 - L.delay;
                entrance = reduceMotion ? 1 :
                    secs <= 0 ? 0 :
                        secs >= SKY_ENTRANCE_SEC ? 1 : skyEase(secs / SKY_ENTRANCE_SEC);
                shift = (1 - entrance) * SKY_ENTRANCE_SHIFT * H;
            }
            if (entrance <= 0.001) {
                continue;
            }
            // Scroll growth x breathing — same numbers the transform used.
            const grow = interp(p, [L.growStart, L.growEnd], [1, L.growTo]);
            const breath = reduceMotion ? 0 : Math.sin(t * ((2 * Math.PI) / L.pulsePeriod) + L.phase);
            const scale = grow * (1 + breath * L.pulseAmp);
            const drift = breath * L.driftAmp;
            paintSkyEllipse(
                W * 0.5 + drift,
                originY + scale * (boxH * (L.originY - 1) + shift),
                scale * L.sizeX * boxW,
                scale * L.sizeY * boxH,
                L.rgb, SUNRISE_STOPS, entrance,
            );
        }

        // Daylight wash (was #max-daylight: scale + opacity from progress).
        const dayAlpha = interp(p, [0.2, 0.34], [0, 1]);
        if (dayAlpha > 0.001) {
            const dayScale = interp(p, [0.2, 0.52], [0.45, 5]);
            paintSkyEllipse(
                W * 0.5,
                originY + dayScale * boxH * 0.02,
                dayScale * 1.10 * boxW,
                dayScale * 0.95 * boxH,
                [255, 255, 255], DAYLIGHT_STOPS, dayAlpha,
            );
        }

        // White lock (was #max-daylight-lock: a solid opacity fade).
        const lockAlpha = interp(p, [0.46, 0.56], [0, 1]);
        if (lockAlpha > 0.001) {
            skyCtx.fillStyle = `rgba(255, 255, 255, ${lockAlpha.toFixed(4)})`;
            skyCtx.fillRect(0, 0, W, H);
        }
    }

    sizeSky();

    // Ambient glow behind Max — navy -> amber -> rose -> daylight white.
    const GLOW_STOPS = [0, 0.18, 0.36, 0.52];
    const GLOW_COLORS = [
        [20, 91, 168, 0.18],
        [253, 135, 1, 0.28],
        [227, 192, 206, 0.40],
        [255, 255, 255, 0.55],
    ];

    /**
     * The glow used to be a solid disc under `filter: blur(64px)`; the filter
     * is gone (see .max-glow in max.scss — no filters in the backdrop, ever),
     * so this now paints the blurred disc's profile directly: a flat plateau
     * out to ~40% (the disc minus ~2 sigma) then a Gaussian-ish tail to the
     * (enlarged) box edge.
     *
     * @param {number} p - Hero progress.
     * @returns {string} radial-gradient() background for the glow.
     */
    function glowGradient(p) {
        const r = interp(p, GLOW_STOPS, GLOW_COLORS.map((c) => c[0])).toFixed(0);
        const g = interp(p, GLOW_STOPS, GLOW_COLORS.map((c) => c[1])).toFixed(0);
        const b = interp(p, GLOW_STOPS, GLOW_COLORS.map((c) => c[2])).toFixed(0);
        const a = interp(p, GLOW_STOPS, GLOW_COLORS.map((c) => c[3]));
        const at = (f) => `rgba(${r}, ${g}, ${b}, ${(a * f).toFixed(3)})`;
        return `radial-gradient(circle closest-side, ${at(1)} 0%, ${at(1)} 40%, ${at(0.62)} 63%, ${at(0.5)} 70%, ${at(0.26)} 80%, ${at(0.1)} 89%, transparent 100%)`;
    }

    // Skip the style write (and its repaint) whenever the gradient is
    // unchanged — p is static while the page sits idle.
    let lastGlowGradient = '';

    // ── Digit field: faint currency symbols in ripple rings ─────────────

    const SYMBOLS = ['$', '€', '£', '¥', '₣', '₹', '₩', '₿', '₺', '₴', '₦', '₱', '₲', '₵', '₸', '₼', '﷼', '৳', '฿', '₫', 'kr', '¢'];
    const CELL = 20;
    const FONT_SIZE = 12;
    const DIGIT_FPS = 30;
    const BASE_ALPHA = 0.6;
    const DROP_X = 0.5;
    const DROP_Y = 1.02;
    const WAVE_FREQ = 7.0;
    const WAVE_SPEED = 1.1;
    const WAVE_SHARPNESS = 4.0;

    const digitField = document.getElementById('max-digit-field');
    const digitCanvas = /** @type {HTMLCanvasElement|null} */ (document.getElementById('max-digit-canvas'));
    const digitCtx = digitCanvas ? digitCanvas.getContext('2d') : null;
    /** @type {{x: number, y: number, char: string, nextSwap: number, mask: number, seed: number, dist: number}[]} */
    let digitCells = [];
    let digitLast = 0;

    /**
     * @param {number} u - Normalized x.
     * @param {number} v - Normalized y.
     * @returns {number} Brightness mask matching the sunrise glow shape.
     */
    function glowMask(u, v) {
        const dx = (u - 0.5) * 1.35;
        const dy = (v - 1.02) * 1.0;
        const d = Math.sqrt(dx * dx + dy * dy);
        const m = Math.max(0, 1 - d / 0.78);
        return Math.pow(m, 1.3);
    }

    // Cap the digit canvas at 1.5x: it paints faint 12px glyphs under other
    // layers, where full retina resolution is invisible but nearly doubles
    // the buffer (a full-viewport canvas). MUST match the self-heal check in
    // drawDigits, or the two would disagree and rebuild every frame.
    const DIGIT_DPR_CAP = 1.5;

    function buildDigits() {
        if (!digitCanvas || !digitCtx) {
            return;
        }
        const dpr = Math.min(window.devicePixelRatio || 1, DIGIT_DPR_CAP);
        const w = digitCanvas.clientWidth;
        const h = digitCanvas.clientHeight;
        digitCanvas.width = Math.round(w * dpr);
        digitCanvas.height = Math.round(h * dpr);
        digitCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        digitCtx.font = `${FONT_SIZE}px monospace`;
        digitCtx.textAlign = 'center';
        digitCtx.textBaseline = 'middle';

        digitCells = [];
        const cols = Math.ceil(w / CELL);
        const rows = Math.ceil(h / CELL);
        const now = performance.now();
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const u = (c + 0.5) / cols;
                const v = (r + 0.5) / rows;
                const mask = glowMask(u, v);
                if (mask < 0.05) {
                    continue;
                }
                const seed = Math.random();
                if (seed > mask * 0.9 + 0.45) {
                    continue;
                }
                const ddx = (u - DROP_X) * 1.35;
                const ddy = v - DROP_Y;
                digitCells.push({
                    x: c * CELL + CELL / 2,
                    y: r * CELL + CELL / 2,
                    char: SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)],
                    nextSwap: now + 1000 + Math.random() * 6000,
                    mask,
                    seed: Math.random() * Math.PI * 2,
                    dist: Math.sqrt(ddx * ddx + ddy * ddy),
                });
            }
        }
    }

    /**
     * @param {number} now - Timestamp from the shared loop.
     */
    function drawDigits(now) {
        if (!digitCanvas || !digitCtx) {
            return;
        }
        if (now - digitLast < 1000 / DIGIT_FPS) {
            return;
        }
        digitLast = now;
        const w = digitCanvas.clientWidth;
        const h = digitCanvas.clientHeight;
        // Self-heal: if the buffer no longer matches the laid-out size (a
        // measurement landed before layout settled, or the canvas resized
        // without a window resize event), rebuild — otherwise the browser
        // stretches the buffer and the 12px glyphs render huge.
        const dpr = Math.min(window.devicePixelRatio || 1, DIGIT_DPR_CAP);
        if (digitCanvas.width !== Math.round(w * dpr) || digitCanvas.height !== Math.round(h * dpr)) {
            buildDigits();
        }
        digitCtx.clearRect(0, 0, w, h);
        const t = now / 1000;
        for (const cell of digitCells) {
            if (!reduceMotion && now >= cell.nextSwap) {
                cell.char = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
                cell.nextSwap = now + 200 + Math.random() * 800;
            }
            const wave = 0.5 + 0.5 * Math.sin(cell.dist * WAVE_FREQ - t * WAVE_SPEED);
            const ripple = reduceMotion ? 0.6 : Math.pow(wave, WAVE_SHARPNESS);
            const twinkle = reduceMotion ? 0.85 : 0.78 + 0.22 * Math.sin(t * 0.7 + cell.seed);
            const a = cell.mask * twinkle * ripple * BASE_ALPHA;
            if (a < 0.008) {
                continue;
            }
            digitCtx.fillStyle = `rgba(255, 244, 224, ${a})`;
            digitCtx.fillText(cell.char, cell.x, cell.y);
        }
    }

    buildDigits();
    window.addEventListener('resize', buildDigits);

    // ── Hero beats 2–4 + glass prompts ───────────────────────────────────

    const center = document.getElementById('max-center');
    const promptEls = /** @type {HTMLElement[]} */ (Array.from(page.querySelectorAll('.max-prompt')));
    const PROMPT_DRIFT = [
        [-1, -1], [1, -1], [1, -0.4], [1, 1], [0.4, 1], [-1, 1], [-1, 0], [-0.5, -1],
        // Desktop-only extras (indices 8, 9).
        [0.4, -1], [-0.6, 1],
    ];

    // ── Persistent Max wrapper (entrance / hold / settle-into-badge) ─────

    const persistentBox = document.getElementById('max-persistent-box');
    const tint = document.getElementById('max-tint');
    const featureLast = document.getElementById('max-feature-last');

    // ── Scroll-speed -> ambient drift boost for the ribbon ───────────────

    const BOOST_MAX = 3.0;
    const VEL_MAX = 1500;
    const VEL_HOLD = 0.35;
    const ATTACK = 14;
    const DECAY = 2.2;

    let driftBoost = 1;
    let velPeak = 0;
    let lastScrollY = window.scrollY;

    // ── The Mobius ribbon (vanilla three.js) ─────────────────────────────

    const GEOMETRY = {
        fins: 120,
        loopRadius: 1.35,
        finLength: 0.95,
        finWidth: 0.05,
        finDepth: 0.18,
        twistTurns: 1,
        bevel: 0.012,
    };
    // Ribbon world size: loopRadius + finLength/2 each side.
    const RIBBON_DIAMETER = 3.65;
    // The "ripple" persona state: one crest travels the ring at constant speed.
    const RIPPLE = {
        duration: 3.5,
        spinLag: 0.16, // crest width
        rippleAngle: 90, // peak lean, degrees
        spinDrift: 10, // ambient per-fin drift, deg/sec
    };
    // Whole-mesh ambient rotation about Z, degrees per second.
    const SPIN_Z = -10;

    const maxCanvas = /** @type {HTMLCanvasElement|null} */ (document.getElementById('max-canvas'));
    let maxReady = false;

    function signalMaxReady() {
        if (maxReady) {
            return;
        }
        maxReady = true;
        page.classList.add('is-ready', 'is-started');
        startedAt = performance.now();
    }

    let startedAt = 0;
    // Safety: the hero copy can never stay hidden if WebGL fails or is slow.
    setTimeout(signalMaxReady, 4000);

    /** @type {THREE.WebGLRenderer|null} */
    let renderer = null;
    /** @type {THREE.Scene|null} */
    let scene = null;
    /** @type {THREE.OrthographicCamera|null} */
    let camera = null;
    /** @type {THREE.InstancedMesh|null} */
    let ribbon = null;
    /** @type {THREE.MeshPhysicalMaterial|null} */
    let ribbonMaterial = null;
    /** @type {{radialDir: THREE.Vector3, base: THREE.Matrix4}[]} */
    let finBases = [];
    let ribbonTime = 0;
    let ribbonDriftT = 0;
    let ribbonFrames = 0;
    // Mobile-only: true while the hero entrance has the box CSS-scaled large,
    // buying the render buffer extra resolution exactly (and only) where the
    // magnification otherwise exposes grain. Starts true — the page loads at
    // the top of the hero. Toggled with hysteresis from the frame loop.

    let ribbonHeroBoost = true;

    const scratchM = new THREE.Matrix4();
    const scratchRot = new THREE.Matrix4();

    function initRibbon() {
        if (!maxCanvas) {
            return;
        }
        let gl = null;
        try {
            gl = maxCanvas.getContext('webgl2') || maxCanvas.getContext('webgl');
        } catch {
            gl = null;
        }
        if (!gl) {
            page.classList.add('no-webgl');
            signalMaxReady();
            return;
        }

        // antialias (MSAA) is OFF on desktop on purpose: the buffer below is
        // already supersampled (needed because the hero CSS-scales the canvas
        // up), which smooths edges better than MSAA would — running both, as
        // the v0 original did, roughly doubles GPU fill/memory for no visible
        // gain and is a large part of what overwhelmed weaker compositors at
        // big window sizes. On TOUCH devices the trade inverts: the canvas is
        // tiny (64vmin of a phone), and tile-based mobile GPUs resolve 4x MSAA
        // on-chip at near-zero cost — it's the cheapest edge quality available
        // exactly where the thin fins otherwise shimmer.
        renderer = new THREE.WebGLRenderer({
            canvas: maxCanvas,
            antialias: coarsePointer,
            alpha: true,
            powerPreference: 'high-performance',
        });
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.05;

        scene = new THREE.Scene();
        camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
        camera.position.set(0, 0, 10);

        scene.add(new THREE.AmbientLight(0xffffff, 0.08));

        // Environment: emissive "lightformers" rendered once through a cube
        // camera into a HalfFloat cube render target — the exact code path of
        // drei's <Environment resolution={256}>/<Lightformer> (drei 10.7.7),
        // so the ribbon's chrome reflections match the v0 reference 1:1.
        const envScene = new THREE.Scene();
        const former = (form, color, intensity, position, scale) => {
            // drei's ring form is ringGeometry(0.25, 0.5); rect is a unit plane.
            const geo = form === 'ring' ?
                new THREE.RingGeometry(0.25, 0.5, 64) :
                new THREE.PlaneGeometry(1, 1);
            const mat = new THREE.MeshBasicMaterial({side: THREE.DoubleSide, toneMapped: false});
            mat.color.set(color).multiplyScalar(intensity);
            const mesh = new THREE.Mesh(geo, mat);
            mesh.position.set(position[0], position[1], position[2]);
            mesh.scale.set(scale[0], scale[1], scale[2]);
            // drei's <Lightformer> aims every former at the origin.
            mesh.lookAt(0, 0, 0);
            envScene.add(mesh);
        };
        former('rect', '#ffffff', 2.2, [0, 4, 3], [10, 6, 1]);
        former('rect', '#e8edf5', 1.1, [-5, 0, 2], [6, 8, 1]);
        former('rect', '#cdd6e6', 0.7, [5, 1, -2], [6, 8, 1]);
        former('ring', '#ffffff', 1.6, [0, 0, 5], [3, 3, 1]);
        const envTarget = new THREE.WebGLCubeRenderTarget(256);
        envTarget.texture.type = THREE.HalfFloatType;
        const cubeCamera = new THREE.CubeCamera(0.1, 1000, envTarget);
        const autoClear = renderer.autoClear;
        renderer.autoClear = true;
        cubeCamera.update(renderer, envScene);
        renderer.autoClear = autoClear;
        scene.environment = envTarget.texture;

        // Instanced fins around the Möbius ring.
        const {fins, loopRadius, finLength, finWidth, finDepth, twistTurns, bevel} = GEOMETRY;
        const finGeo = new RoundedBoxGeometry(
            finWidth, finLength, finDepth, 2,
            Math.min(bevel, finWidth * 0.49, finDepth * 0.49),
        );
        const material = new THREE.MeshPhysicalMaterial({
            color: '#c4c8cd',
            metalness: 1,
            roughness: 0.22,
            clearcoat: 1,
            clearcoatRoughness: 0.12,
            emissive: '#000000',
            emissiveIntensity: 0.45,
            envMapIntensity: 2.2,
        });
        // Route the per-instance color into EMISSIVE only, so the metal keeps
        // full reflectivity (the default chunk tints diffuse).
        material.onBeforeCompile = (shader) => {
            shader.fragmentShader = shader.fragmentShader
                .replace('#include <color_fragment>', '')
                .replace(
                    'vec3 totalEmissiveRadiance = emissive;',
                    'vec3 totalEmissiveRadiance = emissive * vColor.rgb;',
                );
        };

        ribbonMaterial = material;
        ribbon = new THREE.InstancedMesh(finGeo, material, fins);
        finBases = [];
        const white = new THREE.Color(1, 1, 1);
        for (let i = 0; i < fins; i++) {
            const a = (i / fins) * Math.PI * 2;
            const ca = Math.cos(a);
            const sa = Math.sin(a);
            const pos = new THREE.Vector3(ca * loopRadius, sa * loopRadius, 0);
            const tangent = new THREE.Vector3(-sa, ca, 0).normalize();
            const twist = a * twistTurns;
            const width = new THREE.Vector3(ca, sa, 0)
                .multiplyScalar(Math.cos(twist))
                .addScaledVector(new THREE.Vector3(0, 0, 1), Math.sin(twist))
                .normalize();
            const depth = new THREE.Vector3().crossVectors(tangent, width).normalize();
            const base = new THREE.Matrix4().makeBasis(tangent, width, depth).setPosition(pos);
            finBases.push({radialDir: new THREE.Vector3(ca, sa, 0), base});
            ribbon.setMatrixAt(i, base);
            ribbon.setColorAt(i, white);
        }
        if (ribbon.instanceColor) {
            ribbon.instanceColor.needsUpdate = true;
        }
        scene.add(ribbon);

        // Recover gracefully from WebGL context loss.
        maxCanvas.addEventListener('webglcontextlost', (e) => e.preventDefault(), false);

        resizeRibbon();
        window.addEventListener('resize', resizeRibbon);
        // The box is laid out in vmin and the first measurement can land
        // before layout settles — observe the box itself so the buffer always
        // matches the real (untransformed) size.
        if (typeof ResizeObserver !== 'undefined' && persistentBox) {
            new ResizeObserver(resizeRibbon).observe(persistentBox);
        }
    }

    function resizeRibbon() {
        if (!renderer || !camera || !persistentBox) {
            return;
        }
        // offsetWidth ignores the CSS scale transform on the box — critical,
        // or the render would be double-scaled.
        const w = persistentBox.offsetWidth;
        const h = persistentBox.offsetHeight;
        if (w < 1 || h < 1) {
            return;
        }
        // Supersample for the hero's CSS scale-up, but keep it modest: the
        // ribbon's PBR/clearcoat shader is fill-bound, so buffer pixels are the
        // main GPU cost. Desktop: 1.75x capped at 3 (and at a 2048px long
        // side) is still crisp once the ribbon settles. Mobile gets a higher
        // ceiling (2x, cap 4) because its 64vmin canvas is tiny (~250px on a
        // phone) while the entrance CSS-scales the box 3.5x — and during that
        // entrance specifically (`ribbonHeroBoost`, toggled from hero progress
        // with hysteresis in the frame loop) it steps up again to 2.5x/cap 5.
        // That's ~1.4MP at the moment Max fills a phone screen, dropping back
        // to ~0.9MP once he settles small — the boost costs nothing where the
        // ribbon is displayed at a fraction of the buffer anyway. The v0
        // original used 2.5-4x + MSAA everywhere, ~2x the fill for no visible
        // gain at rest.
        const ratio = window.devicePixelRatio || 1;
        const boost = isMobile && ribbonHeroBoost;
        const multiplier = isMobile ? (boost ? 2.5 : 2) : 1.75;
        const maxRatio = isMobile ? (boost ? 5 : 4) : 3;
        const pixelRatio = Math.min(ratio * multiplier, maxRatio, 2048 / Math.max(w, h));
        renderer.setPixelRatio(pixelRatio);
        renderer.setSize(w, h, false);
        camera.left = -w / 2;
        camera.right = w / 2;
        camera.top = h / 2;
        camera.bottom = -h / 2;
        // Contain-fit with headroom for fins that tilt outward in the ripple.
        camera.zoom = (Math.min(w, h) * 0.76) / RIBBON_DIAMETER;
        camera.updateProjectionMatrix();
    }

    // Reflection intensity across the hero. The authored 2.2 reads glaring at
    // the entrance, where the ribbon is magnified ~3x against the dark sky —
    // hold the metal darker there and ease up to full chrome as the daylight
    // wash takes over (from ~0.2 hero progress the sky is already brightening).
    // 0.85 keeps even the brightest drift phase reading as dark chrome — at
    // higher values the white ring former's specular hit still saturates to
    // pure white after ACES tone mapping, which glared against the night sky.
    const ENV_ENTRANCE = 0.85;
    const ENV_FULL = 2.2;

    /**
     * @param {number} dt - Clamped frame delta, seconds.
     * @param {number} p - Smoothed hero progress 0 -> 1.
     */
    function updateRibbon(dt, p) {
        if (!renderer || !scene || !camera || !ribbon) {
            return;
        }
        if (ribbonMaterial) {
            ribbonMaterial.envMapIntensity = interp(p, [0, 0.3], [ENV_ENTRANCE, ENV_FULL]);
        }
        const DEG = Math.PI / 180;
        ribbonTime += dt;
        ribbonDriftT += dt * driftBoost;

        const {fins} = GEOMETRY;
        // Crest position: constant speed, wraps every RIPPLE.duration seconds.
        const u = ((ribbonTime / RIPPLE.duration) % 1 + 1) % 1;
        const peak = RIPPLE.rippleAngle * DEG;
        const drift = ribbonDriftT * (RIPPLE.spinDrift * DEG);
        const W = RIPPLE.spinLag;

        ribbon.rotation.z = ribbonDriftT * (SPIN_Z * DEG);

        for (let i = 0; i < fins; i++) {
            const bp = i / fins;
            let dd = Math.abs(bp - u);
            dd = Math.min(dd, 1 - dd);
            const g = Math.exp(-((dd / W) * (dd / W)) * 4);
            const tilt = peak * g + drift;
            scratchRot.makeRotationX(tilt);
            scratchM.copy(finBases[i].base).multiply(scratchRot);
            ribbon.setMatrixAt(i, scratchM);
        }
        ribbon.instanceMatrix.needsUpdate = true;
        renderer.render(scene, camera);

        // Reveal a few frames in so the shader-compile stall stays hidden and
        // the fade-in starts on already-smooth motion.
        ribbonFrames += 1;
        if (ribbonFrames === 3) {
            signalMaxReady();
        }
    }

    // ── Testimonial drum ─────────────────────────────────────────────────

    const drumStage = document.getElementById('max-drum-stage');
    const drum = document.getElementById('max-drum');
    const cardEls = /** @type {HTMLElement[]} */ (Array.from(page.querySelectorAll('.max-quote-card')));
    const CARD_COUNT = cardEls.length;
    const STEP = 22;
    const TOTAL = CARD_COUNT * STEP;
    const GAP = 8;
    const DWELL_MS = 2200;
    const TURN_S = 1.5;
    const easeDrum = cubicBezier(0.65, 0, 0.35, 1);
    const DRAG_SENSITIVITY = 2.4;
    const PROJECT_S = 0.16;
    const FLICK_DEG_PER_SEC = 90;

    let drumRotation = 0;
    let drumRadius = 0;
    let drumActive = false;
    let drumResumeAt = 0;
    let drumDwellEnd = performance.now() + DWELL_MS;
    /** @type {{from: number, to: number, start: number} | null} */
    let drumTween = null;
    /** @type {{target: number, velocity: number} | null} */
    let drumSpring = null;

    let dragging = false;
    let dragStartX = 0;
    let dragStartRot = 0;
    let dragStartTime = 0;
    let dragLastX = 0;

    function measureDrum() {
        const first = cardEls[0];
        if (!first) {
            return;
        }
        // offsetWidth: the live 3D transform makes getBoundingClientRect
        // return the perspective-projected (distorted) box.
        const w = first.offsetWidth;
        if (w < 1) {
            return;
        }
        drumRadius = (w + GAP) / (2 * Math.sin((STEP * Math.PI) / 360));
        if (drum) {
            drum.style.transform = `translateZ(${(-drumRadius).toFixed(1)}px)`;
        }
    }

    if (drumStage && CARD_COUNT) {
        requestAnimationFrame(measureDrum);
        if (typeof ResizeObserver !== 'undefined') {
            new ResizeObserver(measureDrum).observe(cardEls[0]);
        }
        window.addEventListener('resize', measureDrum);
        window.addEventListener('orientationchange', measureDrum);

        if (typeof IntersectionObserver !== 'undefined') {
            new IntersectionObserver(([entry]) => {
                drumActive = entry.isIntersecting && document.visibilityState === 'visible';
            }, {rootMargin: '20% 0px'}).observe(quoteSection || drumStage);
        } else {
            drumActive = true;
        }

        drumStage.addEventListener('pointerdown', (e) => {
            dragging = true;
            drumTween = null;
            drumSpring = null;
            dragStartX = e.clientX;
            dragLastX = e.clientX;
            dragStartRot = drumRotation;
            dragStartTime = performance.now();
            drumStage.setPointerCapture(e.pointerId);
        });
        drumStage.addEventListener('pointermove', (e) => {
            if (!dragging || drumRadius === 0) {
                return;
            }
            const degPerPx = (180 / (Math.PI * drumRadius)) * DRAG_SENSITIVITY;
            drumRotation = dragStartRot + (e.clientX - dragStartX) * degPerPx;
            dragLastX = e.clientX;
        });
        const endDrag = (e) => {
            if (!dragging) {
                return;
            }
            dragging = false;
            try {
                drumStage.releasePointerCapture(e.pointerId);
            } catch {
                // pointer already released
            }
            const dx = dragLastX - dragStartX;
            const dt = Math.max(1, performance.now() - dragStartTime);
            const degPerPx = (180 / (Math.PI * drumRadius)) * DRAG_SENSITIVITY;
            const velDegPerSec = (dx / dt) * degPerPx * 1000;
            const startStep = Math.round(dragStartRot / STEP) * STEP;
            // Project the fling's momentum, then snap to the nearest card.
            const projected = drumRotation + velDegPerSec * PROJECT_S;
            let target = Math.round(projected / STEP) * STEP;
            if (Math.abs(velDegPerSec) > FLICK_DEG_PER_SEC && target === startStep) {
                target = startStep + (velDegPerSec > 0 ? STEP : -STEP);
            }
            drumResumeAt = Date.now() + 1500;
            drumSpring = {target, velocity: velDegPerSec};
        };
        drumStage.addEventListener('pointerup', endDrag);
        drumStage.addEventListener('pointercancel', endDrag);
        drumStage.addEventListener('pointerleave', endDrag);
    }

    /**
     * @param {number} now - Timestamp.
     * @param {number} dt - Frame delta, seconds.
     */
    function updateDrum(now, dt) {
        if (!drum || drumRadius === 0 || !drumActive) {
            return;
        }

        // Auto-advance: dwell with a card locked centre-stage, then turn.
        if (!dragging && !drumSpring && !reduceMotion && Date.now() >= drumResumeAt) {
            if (drumTween) {
                const t = clamp01((now - drumTween.start) / (TURN_S * 1000));
                drumRotation = lerp(drumTween.from, drumTween.to, easeDrum(t));
                if (t >= 1) {
                    drumTween = null;
                    drumDwellEnd = now + DWELL_MS;
                }
            } else if (now >= drumDwellEnd) {
                drumTween = {
                    from: drumRotation,
                    to: Math.round(drumRotation / STEP) * STEP + STEP,
                    start: now,
                };
            }
        }

        // Post-drag momentum: a spring carries the release velocity to rest.
        if (drumSpring && !dragging) {
            const s = drumSpring;
            const n = Math.max(1, Math.ceil(dt / 0.008));
            const h = dt / n;
            for (let i = 0; i < n; i++) {
                const accel = (s.target - drumRotation) * 100 - 24 * s.velocity;
                s.velocity += accel * h;
                drumRotation += s.velocity * h;
            }
            if (Math.abs(s.target - drumRotation) < 0.4 && Math.abs(s.velocity) < 1) {
                drumRotation = s.target;
                drumSpring = null;
                drumDwellEnd = now + DWELL_MS;
            }
        }

        // Place every card on the cylinder from the single rotation value.
        for (let i = 0; i < CARD_COUNT; i++) {
            const el = cardEls[i];
            let a = (-i * STEP + drumRotation) % TOTAL;
            if (a > TOTAL / 2) {
                a -= TOTAL;
            }
            if (a < -TOTAL / 2) {
                a += TOTAL;
            }
            el.style.transform = `rotateY(${a.toFixed(2)}deg) translateZ(${drumRadius.toFixed(1)}px)`;
            const off = Math.abs(a) / STEP;
            const visible = off < 2.6;
            el.style.opacity = !visible ? '0' : (1 - Math.min(off / 2.6, 1) * 0.55).toFixed(3);
            el.style.zIndex = String(200 - Math.round(off * 10));
            el.style.pointerEvents = off < 0.5 ? 'auto' : 'none';
        }
    }

    // ── The shared frame loop ────────────────────────────────────────────

    const bgScaleFor = () => (isMobile ? 0.56 : 0.44);
    let lastFrame = performance.now();
    // The browser restores the previous scroll position ASYNCHRONOUSLY after a
    // reload — up to a second or more in. If the springs eased toward that
    // jump they would race the entire hero timeline (sunrise, daylight, tint,
    // Max entrance) at warp speed on screen. During this window every spring
    // snaps straight to its target instead, so whatever position the browser
    // lands on renders instantly-correct.
    const snapUntil = performance.now() + 1500;

    /**
     * @param {number} now - rAF timestamp.
     */
    function frame(now) {
        requestAnimationFrame(frame);
        const rawDt = (now - lastFrame) / 1000;
        lastFrame = now;
        // Clamp so a tab-switch / compile stall resumes smoothly instead of
        // jumping the animation forward.
        const dt = Math.min(rawDt, 0.05);
        if (document.visibilityState !== 'visible') {
            return;
        }

        // Self-heal the ready/started classes: the dev server's CSS live
        // reload patches the DOM and can wipe JS-added classes, which would
        // blank the sunrise (its entrance is gated on .is-started).
        if (maxReady && !page.classList.contains('is-started')) {
            page.classList.add('is-ready', 'is-started');
        }

        // Resize-storm release edge: the freeze window just expired, so layout
        // has settled. Snap the springs to the fresh targets (read below) and
        // re-run the intro toggle, which was also held during the storm.
        const frozen = now < resizeFreezeUntil;
        if (resizeFrozen && !frozen) {
            snapSprings = true;
            updateIntroVisibility();
        }
        resizeFrozen = frozen;

        // Recompute scroll-derived targets from the CURRENT scroll position,
        // every frame. The 'scroll' event fires asynchronously relative to rAF,
        // so relying on it alone let a frame paint the repositioned sticky
        // stage with stale progress — a one-frame flash of the wrong beat after
        // a jump. Reading here keeps progress in lockstep with what's on screen.
        // (No-ops while a resize storm has the targets frozen.)
        readScroll();

        // Snap (don't ease) during the post-load restore window OR whenever the
        // scroll position jumped discontinuously — either way, easing would
        // flash every intermediate beat while the spring crosses the hero.
        if (now < snapUntil || snapSprings) {
            heroProgress.value = heroProgress.target;
            heroProgress.velocity = 0;
            tintOpacity.value = tintOpacity.target;
            tintOpacity.velocity = 0;
            badgeSettle.value = badgeSettle.target;
            badgeSettle.velocity = 0;
            // A restoration/anchor jump is not a user fling — don't spin Max up.
            lastScrollY = window.scrollY;
            velPeak = 0;
            snapSprings = false;
        }

        const p = heroProgress.step(dt);
        const tintV = tintOpacity.step(dt);
        const settle = badgeSettle.step(dt);
        const t = now / 1000;

        // Mobile hero-entrance resolution boost (see resizeRibbon). The box
        // finishes shrinking to its resting scale by p=0.4; hysteresis keeps
        // the buffer from re-allocating when a scroll settles on the boundary.
        if (isMobile) {
            if (ribbonHeroBoost && p > 0.52) {
                ribbonHeroBoost = false;
                resizeRibbon();
            } else if (!ribbonHeroBoost && p < 0.44) {
                ribbonHeroBoost = true;
                resizeRibbon();
            }
        }

        // Scroll velocity -> ambient drift boost (held peak, fast attack /
        // slow decay), read by the ribbon every frame.
        {
            const cur = window.scrollY;
            // Scroll-anchoring corrections during a resize storm move scrollY
            // without the user scrolling — don't let them spin the ribbon up.
            const instVel = frozen ? 0 : Math.abs(cur - lastScrollY) / Math.max(dt, 0.001);
            lastScrollY = cur;
            if (reduceMotion) {
                driftBoost = 1;
            } else {
                velPeak = Math.min(VEL_MAX, Math.max(instVel, velPeak * Math.exp(-dt / VEL_HOLD)));
                const target = 1 + Math.min(1, velPeak / VEL_MAX) * (BOOST_MAX - 1);
                const rate = target > driftBoost ? ATTACK : DECAY;
                driftBoost += (target - driftBoost) * (1 - Math.exp(-dt * rate));
            }
        }

        // The sky (sunrise gradients, daylight wash, white lock) — one canvas
        // repaint instead of transforms on 8 giant composited layers.
        drawSky(p, now, frozen);
        if (glow) {
            const grad = glowGradient(p);
            if (grad !== lastGlowGradient) {
                lastGlowGradient = grad;
                glow.style.background = grad;
            }
        }

        // Digit field: scroll dissolve x entrance fade (2.4s, 1.6s after start).
        if (digitField) {
            const scrollFade = interp(p, [0.04, 0.36], [1, 0]);
            const entrance = startedAt === 0 ? 0 : clamp01((now - startedAt - 1600) / 2400);
            digitField.style.opacity = (scrollFade * entrance).toFixed(3);
            if (scrollFade * entrance > 0.01) {
                drawDigits(now);
            }
        }

        // Beat 2 centered headline.
        if (center) {
            center.style.opacity = interp(p, [0.3, 0.38, 0.5, 0.58], [0, 1, 1, 0]).toFixed(3);
            center.style.transform = `translateY(${interp(p, [0.3, 0.4], [10, 0]).toFixed(2)}%)`;
        }

        // Beat 3 glass prompts: staggered reveal + gentle float.
        for (let i = 0; i < promptEls.length; i++) {
            // Cap the stagger so the later cards (incl. the desktop-only extras
            // at indices 8–9) still fully reveal before the shared fade-out at
            // 0.74 — an uncapped start would cross the fade and flicker.
            const start = Math.min(0.7, 0.52 + i * 0.022);
            const el = promptEls[i];
            el.style.opacity = interp(p, [start, start + 0.025, 0.74, 0.82], [0, 1, 1, 0]).toFixed(3);
            if (!reduceMotion) {
                const dur = 6 + i * 0.4;
                const phase = Math.max(0, t - i * 0.25) / dur;
                const wobble = 0.5 * (1 - Math.cos(2 * Math.PI * phase));
                const [dx, dy] = PROMPT_DRIFT[i] || [0, 0];
                el.style.transform = `translate(${(dx * 10 * wobble).toFixed(2)}px, ${(dy * 10 * wobble).toFixed(2)}px)`;
            }
        }

        // (The product video is now a plain in-flow section below the hero —
        // no scroll-driven code here.)

        // White -> beige tint + last feature row blend (same scroll window).
        if (tint) {
            tint.style.opacity = tintV.toFixed(3);
        }
        if (featureLast) {
            featureLast.style.setProperty('--max-blend', rawBlend.toFixed(3));
        }

        // Flip the nav in step with the backdrop behind it. The top of the
        // viewport whitens as the daylight-lock fades in over ~p 0.46–0.56, so
        // dark (ink) nav text is only safe once that lock is essentially opaque
        // (p >= ~0.56). Flipping back to light text a touch earlier (p < 0.52)
        // keeps ink text from ever sitting on the re-darkening backdrop on the
        // way up — that was the black-on-black band. The gap between the two
        // thresholds is the anti-chatter hysteresis.
        if (navEl) {
            if (navOverDark && p > 0.34) {
                navOverDark = false;
                navEl.classList.remove('is-max-nav-over-dark');
            } else if (!navOverDark && p < 0.3) {
                navOverDark = true;
                navEl.classList.add('is-max-nav-over-dark');
            }
        }

        // Persistent Max: entrance -> hold -> settle into the badge cutout.
        if (persistentBox) {
            const heroScale = interp(p, [0, 0.4], [isMobile ? 3.5 : 3.0, 0.72]);
            const scale = heroScale + (bgScaleFor() - heroScale) * settle;
            const y = interp(p, [0, 0.4], [isMobile ? 148 : 108, 0]);
            const dim = interp(p, [0.85, 1], [1, 0.25]);
            const opacity = dim + (1 - dim) * settle;
            persistentBox.style.transform = `translateY(${y.toFixed(2)}%) scale(${scale.toFixed(4)})`;
            persistentBox.style.opacity = opacity.toFixed(3);
        }

        updateRibbon(reduceMotion ? 0 : dt, p);
        updateDrum(now, dt);
    }

    initRibbon();
    requestAnimationFrame(frame);
})();
